import { Badge, Box, Checkbox, Flex, Input, Text } from '@chakra-ui/react'
import { Dispatch, ReactNode, SetStateAction, useState } from 'react'

import { ActionButton } from '../../../../components/ActionButton'
import { BankSelect } from '../../../../components/BankSelect'
import { FormField } from '../../../../components/FormField'
import { MaskedInput, MoneyInput, PIX_KEY_MASKS } from '../../../../components/MaskedInput'
import { SectionTitle } from '../../../../components/SectionCard'
import { FormSelect } from '../../../../components/Select'
import { formatCurrency, PixKeyType } from '../../../../helpers'
import { EstablishmentFeeType } from '../../../../services/Event/types'
import { pixKeyTypeOptions } from '../../EventCoupons/couponLabels'
import {
  anyPayout,
  baseProduct,
  FEE_EQUALS_PRICE,
  feeEqualsPrice,
  feePerUnit,
  FEE_TYPE_OPTIONS,
  IEstablishmentForm,
  IEstablishmentProductForm,
  withoutErrorsUnder
} from './establishmentForm'

interface IProps<T extends IEstablishmentForm> {
  establishments: T[]
  onChange: (establishments: T[]) => void
  /** Raiz das chaves de erro da API: `establishments` no evento, `establishment_templates` na série. */
  errorKey: string
  fieldErrors: Record<string, string[]>
  /** Para tirar os erros de uma linha removida, que senão passariam para a linha seguinte. */
  setFieldErrors: Dispatch<SetStateAction<Record<string, string[]>>>
  saving: boolean
  createEstablishment: () => T
  /** Conteúdo extra no fim de cada cartão — o "Aplicar às edições" da série. */
  renderExtra?: (establishment: T, index: number) => ReactNode
}

/**
 * Editor de "Produtos e Serviços" (ABRA-317), o mesmo no evento e na série. Cada estabelecimento é
 * um cartão com seus produtos em linhas repetidas (por isso os campos `sm`, a exceção de densidade
 * do `TicketsTab`) e, quando algum produto deixa repasse, os dados bancários — todos opcionais.
 *
 * A taxa da ABRA sai do repasse: o participante paga o preço, a ABRA fica com a taxa e o
 * estabelecimento com o resto. Não há tipo padrão, e "Sem taxa" é a única forma de vender sem taxa.
 * O produto gratuito (ABRA-399) é o preço 0, marcado pela caixa "Gratuito": ele não tem taxa. A
 * oficina/workshop (também ABRA-399) é a caixa embaixo do nome: o app do parceiro pede, na venda, o
 * nome de quem ocupa cada vaga, e o estoque passa a ser o número de vagas.
 */
export function EstablishmentsEditor<T extends IEstablishmentForm>({
  establishments,
  onChange,
  errorKey,
  fieldErrors,
  setFieldErrors,
  saving,
  createEstablishment,
  renderExtra
}: IProps<T>) {
  const update = (index: number, patch: Partial<T>) =>
    onChange(establishments.map((establishment, i) => (i === index ? { ...establishment, ...patch } : establishment)))

  const updateProduct = (index: number, productIndex: number, patch: Partial<IEstablishmentProductForm>) => {
    update(index, {
      products: establishments[index].products.map((product, j) =>
        j === productIndex ? { ...product, ...patch } : product
      )
    } as Partial<T>)
    // Trocar o tipo da taxa zera o valor: o erro da API sobre a taxa anterior ("entre 1% e 100%")
    // ficaria na tela embaixo de um campo que já não é o mesmo.
    if ('fee_type' in patch) {
      setFieldErrors((prev) => withoutErrorsUnder(prev, `${errorKey}.${index}.products.${productIndex}.fee_`))
    }
    if ('kind' in patch) {
      setFieldErrors((prev) => withoutErrorsUnder(prev, `${errorKey}.${index}.products.${productIndex}.kind`))
    }
    // Marcar ou desmarcar "Gratuito" troca o preço junto com a taxa: o erro do preço anterior também sai.
    if ('price' in patch && 'fee_type' in patch) {
      setFieldErrors((prev) => withoutErrorsUnder(prev, `${errorKey}.${index}.products.${productIndex}.price`))
    }
  }

  const error = (path: string) => fieldErrors[`${errorKey}.${path}`]

  const hasBankErrors = (prefix: string) =>
    Object.keys(fieldErrors).some((key) => key.startsWith(prefix) && /^(bank_|pix_)/.test(key.slice(prefix.length)))

  // Os erros da API vêm por índice (`establishments.1.name`): ao remover uma linha, as de baixo
  // sobem e herdariam o erro da removida. As mensagens agrupadas (que citam o nome) ficam.
  const removeEstablishment = (index: number) => {
    onChange(establishments.filter((_, i) => i !== index))
    setFieldErrors((prev) => withoutErrorsUnder(prev, `${errorKey}.`))
  }

  const removeProduct = (index: number, productIndex: number) => {
    update(index, {
      products: establishments[index].products.filter((_, j) => j !== productIndex)
    } as Partial<T>)
    setFieldErrors((prev) => withoutErrorsUnder(prev, `${errorKey}.${index}.products.`))
  }

  return (
    <Flex direction="column" gap={5}>
      <ActionButton
        action="create"
        label="Adicionar estabelecimento"
        showLabel
        hideTooltip
        alignSelf="flex-start"
        isDisabled={saving}
        onClick={() => onChange([...establishments, createEstablishment()])}
      />

      {fieldErrors[errorKey] && (
        <Text color="red.500" fontSize="sm">
          {fieldErrors[errorKey].join(' ')}
        </Text>
      )}

      {establishments.map((establishment, index) => (
        <Box key={establishment._key} border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
          <Flex justifyContent="space-between" alignItems="center" mb={3} gap={3}>
            <Text fontWeight="bold" fontSize="sm">
              Estabelecimento #{index + 1}
            </Text>
            <Flex alignItems="center" gap={2}>
              {establishment.has_orders && <Badge colorScheme="green">Com vendas</Badge>}
              <ActionButton
                action="delete"
                label={establishment.has_orders ? 'Já tem vendas e não pode ser removido' : 'Remover estabelecimento'}
                mr={0}
                isDisabled={saving || establishment.has_orders}
                onClick={() => removeEstablishment(index)}
              />
            </Flex>
          </Flex>

          <FormField label="Nome do estabelecimento" error={error(`${index}.name`)} isRequired mb={4}>
            <Input
              size="sm"
              value={establishment.name}
              placeholder="EX: Café da Praça"
              maxLength={255}
              onChange={(e) => update(index, { name: e.target.value } as Partial<T>)}
              disabled={saving}
            />
          </FormField>

          <SectionTitle variant="mini">Produtos e serviços</SectionTitle>
          {fieldErrors[`${errorKey}.${index}.products`] && (
            <Text color="red.500" fontSize="sm" mb={2}>
              {fieldErrors[`${errorKey}.${index}.products`].join(' ')}
            </Text>
          )}

          <Flex direction="column" gap={3}>
            {establishment.products.map((product, productIndex) => (
              <ProductRow
                key={product._key}
                product={product}
                errorPath={(field) => error(`${index}.products.${productIndex}.${field}`)}
                saving={saving}
                onChange={(patch) => updateProduct(index, productIndex, patch)}
                onRemove={() => removeProduct(index, productIndex)}
              />
            ))}
          </Flex>

          <ActionButton
            action="create"
            label="Adicionar produto ou serviço"
            showLabel
            hideTooltip
            variant="ghost"
            mt={2}
            isDisabled={saving}
            onClick={() => update(index, { products: [...establishment.products, baseProduct()] } as Partial<T>)}
          />

          {/* Também com erro num campo bancário: escondido, ele seguiria recusando o salvar. */}
          {(anyPayout(establishment) || hasBankErrors(`${errorKey}.${index}.`)) && (
            <BankFields
              establishment={establishment}
              errorPath={(field) => error(`${index}.${field}`)}
              saving={saving}
              onChange={(patch) => update(index, patch as Partial<T>)}
            />
          )}

          {renderExtra?.(establishment, index)}
        </Box>
      ))}
    </Flex>
  )
}

interface IProductRowProps {
  product: IEstablishmentProductForm
  errorPath: (field: string) => string[] | undefined
  saving: boolean
  onChange: (patch: Partial<IEstablishmentProductForm>) => void
  onRemove: () => void
}

function ProductRow({ product, errorPath, saving, onChange, onRemove }: IProductRowProps) {
  const fee = feePerUnit(product)
  // A taxa que leva o preço inteiro é a opção "Sem repasse" (ABRA-399): o campo avisa antes da API.
  const feeValueError = errorPath('fee_value') ?? (feeEqualsPrice(product) ? FEE_EQUALS_PRICE : undefined)
  // O `MoneyInput` nunca produz 0 (o zero à esquerda some), então preço 0 só vem desta caixa ou da API.
  const free = product.price === 0
  const workshop = product.kind === 'workshop'
  const sold = product.sold_quantity > 0
  // Uma venda cancelada não conta como vendida, mas ainda impede remover o produto.
  const locked = sold || product.has_orders

  return (
    <Box>
      <Flex gap={3} alignItems="flex-start">
        <Box flex="3">
          <FormField label="Nome" error={errorPath('name')} isRequired>
            <Input
              size="sm"
              value={product.name}
              placeholder="EX: Café espresso"
              maxLength={255}
              onChange={(e) => onChange({ name: e.target.value })}
              disabled={saving}
            />
          </FormField>
          {/* Num `FormField` próprio, sem rótulo nem `isRequired`: ele dá à caixa um lugar para o erro da
              API e para a explicação, e fora do `FormField` do nome o `isRequired` deste não chegaria à
              caixa como `required` nativo. O tipo trava com a primeira venda, mesmo cancelada, como o
              botão de remover: as vagas vendidas já têm (ou não têm) nome. */}
          <FormField
            error={errorPath('kind')}
            helperText={
              product.has_orders
                ? 'Já teve vendas (mesmo canceladas): o tipo não pode mudar.'
                : workshop
                  ? 'O app pede o nome de cada inscrito na venda.'
                  : undefined
            }
            mt={1}
          >
            <Checkbox
              isChecked={workshop}
              onChange={(e) => onChange({ kind: e.target.checked ? 'workshop' : 'product' })}
              isDisabled={saving || product.has_orders}
            >
              Oficina/Workshop
            </Checkbox>
          </FormField>
        </Box>
        <Box flex="2">
          <FormField label="Preço" error={errorPath('price')} isRequired={!free}>
            {free ? (
              <Input size="sm" value="Grátis" isReadOnly isDisabled={saving} />
            ) : (
              <MoneyInput
                size="sm"
                value={product.price}
                onChange={(price) => onChange({ price })}
                isDisabled={saving}
              />
            )}
          </FormField>
          {/* Fora de qualquer `FormField`: não há erro nem ajuda que seja só dela (o erro do preço aparece
              no campo acima), e o `isRequired` de um `FormField` chegaria à caixa como `required` nativo.
              Grátis não tem taxa; desmarcar volta ao preço e ao tipo da taxa por escolher. Não trava com
              venda, ao contrário do tipo: o preço nunca travou, e a venda guarda o preço e a taxa de quando
              foi feita (editar o produto não muda o que já foi vendido). */}
          <Checkbox
            mt={1}
            isChecked={free}
            onChange={(e) =>
              onChange(
                e.target.checked
                  ? { price: 0, fee_type: 'none', fee_value: null }
                  : { price: null, fee_type: '', fee_value: null }
              )
            }
            isDisabled={saving}
          >
            Gratuito
          </Checkbox>
        </Box>
        <FormField
          label={workshop ? 'Vagas' : 'Estoque'}
          error={errorPath('stock')}
          helperText={sold ? `Vendidos: ${product.sold_quantity}` : undefined}
          flex="1.5"
        >
          <Input
            size="sm"
            type="number"
            // Sem `min` e com `step="any"`: a validação nativa travaria o Salvar em silêncio com a
            // aba escondida. A API recusa estoque quebrado ou abaixo do vendido, e a mensagem volta
            // para este campo.
            step="any"
            placeholder="Ilimitado"
            value={product.stock}
            onChange={(e) => onChange({ stock: e.target.value === '' ? '' : Number(e.target.value) })}
            disabled={saving}
          />
        </FormField>
        {/* ABRA-399 — conta tudo o que o participante levou do produto, de qualquer cartão. A venda
            no checkout não confere: lá o cartão já foi cobrado. */}
        <FormField label="Máx. por pessoa" error={errorPath('max_per_buyer')} flex="2">
          <Input
            size="sm"
            type="number"
            // Sem `min`, pelo mesmo motivo do estoque: a API responde no campo.
            step="any"
            placeholder="Sem limite"
            value={product.max_per_buyer}
            onChange={(e) => onChange({ max_per_buyer: e.target.value === '' ? '' : Number(e.target.value) })}
            disabled={saving}
          />
        </FormField>
        {/* Grátis não tem taxa: as duas colunas ficam vazias, e a linha continua alinhada com as outras. */}
        {free ? (
          <Box flex="2" />
        ) : (
          <FormField label="Taxa da ABRA" error={errorPath('fee_type')} isRequired flex="2">
            <FormSelect
              size="sm"
              value={product.fee_type}
              options={FEE_TYPE_OPTIONS}
              // Trocar o tipo zera o valor: 10% relido como R$ 10,00 não quer dizer a mesma coisa.
              onChange={(value) => onChange({ fee_type: value as EstablishmentFeeType | '', fee_value: null })}
              isDisabled={saving}
            />
          </FormField>
        )}
        <Box flex="1.5">
          {product.fee_type === 'percent' && (
            <FormField label="Taxa (%)" error={feeValueError} isRequired>
              <PercentInput
                value={product.fee_value}
                onChange={(feeValue) => onChange({ fee_value: feeValue })}
                isDisabled={saving}
              />
            </FormField>
          )}
          {product.fee_type === 'fixed' && (
            <FormField label="Valor da taxa" error={feeValueError} isRequired>
              <MoneyInput
                size="sm"
                value={product.fee_value}
                onChange={(feeValue) => onChange({ fee_value: feeValue })}
                isDisabled={saving}
              />
            </FormField>
          )}
        </Box>
        <ActionButton
          action="delete"
          label={locked ? 'Já tem vendas e não pode ser removido' : 'Remover produto'}
          mt={7}
          mr={0}
          isDisabled={saving || locked}
          onClick={onRemove}
        />
      </Flex>
      {product.fee_type === 'no_payout' && !free ? (
        <Text variant="caption" mt={1}>
          Todo o valor fica com a ABRA: o estabelecimento não recebe repasse deste produto.
        </Text>
      ) : (
        fee !== null &&
        product.price !== null &&
        !free && (
          <Text variant="caption" mt={1}>
            Por unidade: ABRA fica com {formatCurrency(fee)} e o estabelecimento recebe{' '}
            {formatCurrency(Math.max(0, product.price - fee))}.
          </Text>
        )
      )}
    </Box>
  )
}

interface IPercentInputProps {
  value: number | null
  onChange: (value: number | null) => void
  isDisabled: boolean
}

/** Até 3 dígitos inteiros e 2 decimais, com vírgula ou ponto — o que a API grava. */
const PERCENT_TEXT = /^\d{0,3}([.,]\d{0,2})?$/

/**
 * A taxa em porcentagem vai de 1% a 99,99% (`EstablishmentFeeHelper` na API). O 100 ainda dá para
 * digitar: é ele que faz o campo mostrar o aviso de usar "Sem repasse" (ABRA-399).
 */
const MAX_PERCENT = 100

/**
 * A porcentagem da taxa. É um campo de texto, e não o `NumberInput` do Chakra: controlado pelo
 * número ele reescrevia "12." como "12" a cada tecla, e o contador dele lê "12,5" como 125 (as
 * setas e o Home/End mudavam o valor para 100%). O texto digitado fica aqui, com vírgula ou ponto.
 * Acima de 100 a tecla é recusada; abaixo de 1 não dá para recusar enquanto se digita ("1,5" passa
 * por "1," e "0,5" por "0"), e a API responde no próprio campo ao salvar.
 */
function PercentInput({ value, onChange, isDisabled }: IPercentInputProps) {
  const [text, setText] = useState(value === null ? '' : String(value).replace('.', ','))

  return (
    <Input
      size="sm"
      inputMode="decimal"
      placeholder="10"
      value={text}
      onChange={(e) => {
        const next = e.target.value.trim()
        if (!PERCENT_TEXT.test(next)) return
        const number = Number(next.replace(',', '.'))
        if (number > MAX_PERCENT) return
        setText(next)
        onChange(next === '' || isNaN(number) ? null : number)
      }}
      disabled={isDisabled}
    />
  )
}

interface IBankFieldsProps {
  establishment: IEstablishmentForm
  errorPath: (field: string) => string[] | undefined
  saving: boolean
  onChange: (patch: Partial<IEstablishmentForm>) => void
}

/** Conta que recebe o repasse — os mesmos campos do parceiro do cupom, todos opcionais. */
function BankFields({ establishment, errorPath, saving, onChange }: IBankFieldsProps) {
  const pixKeyMask = establishment.pix_key_type ? PIX_KEY_MASKS[establishment.pix_key_type] : null

  return (
    <Box mt={4}>
      <SectionTitle variant="mini">Dados bancários para o repasse (opcional)</SectionTitle>
      <Flex direction="column" gap={3}>
        <FormField label="Banco" error={errorPath('bank_code')}>
          <BankSelect
            code={establishment.bank_code}
            name={establishment.bank_name}
            onChange={(bank_code, bank_name) => onChange({ bank_code, bank_name })}
            isDisabled={saving}
            size="sm"
          />
        </FormField>
        <Flex gap={3}>
          <FormField label="Agência" error={errorPath('bank_agency')}>
            <Input
              size="sm"
              value={establishment.bank_agency ?? ''}
              placeholder="EX: 0001"
              maxLength={20}
              onChange={(e) => onChange({ bank_agency: e.target.value || null })}
              disabled={saving}
            />
          </FormField>
          <FormField label="Conta" error={errorPath('bank_account')}>
            <Input
              size="sm"
              value={establishment.bank_account ?? ''}
              placeholder="EX: 12345-6"
              maxLength={20}
              onChange={(e) => onChange({ bank_account: e.target.value || null })}
              disabled={saving}
            />
          </FormField>
          <FormField label="Nome do titular" error={errorPath('bank_holder_name')}>
            <Input
              size="sm"
              value={establishment.bank_holder_name ?? ''}
              placeholder="Quem recebe o repasse"
              maxLength={255}
              onChange={(e) => onChange({ bank_holder_name: e.target.value || null })}
              disabled={saving}
            />
          </FormField>
        </Flex>
        <Flex gap={3}>
          <FormField label="Tipo da chave Pix" error={errorPath('pix_key_type')} flex="1">
            <FormSelect
              size="sm"
              value={establishment.pix_key_type ?? ''}
              options={pixKeyTypeOptions}
              placeholder="Selecione o tipo"
              // Trocar o tipo zera a chave: um CPF relido como e-mail não quer dizer nada.
              onChange={(value) => onChange({ pix_key_type: (value || null) as PixKeyType | null, pix_key: null })}
              isDisabled={saving}
            />
          </FormField>
          <FormField label="Chave Pix" error={errorPath('pix_key')} flex="2">
            <MaskedInput
              size="sm"
              mask={pixKeyMask}
              value={establishment.pix_key}
              onChange={(pix_key) => onChange({ pix_key: pix_key || null })}
              isDisabled={saving || (!establishment.pix_key_type && !establishment.pix_key)}
              {...(pixKeyMask ? {} : { placeholder: 'Selecione o tipo da chave' })}
            />
          </FormField>
        </Flex>
      </Flex>
    </Box>
  )
}
